/**
 * Logger Utility
 *
 * Centralized logging with environment-aware output.
 *
 * @package MultilingualPressZone
 * @since 1.0.0
 */

const isDevelopment = process.env.NODE_ENV !== 'production';

/**
 * Logger class
 */
class Logger {
    constructor(namespace = 'MultilingualPressZone') {
        this.namespace = namespace;
        this.enabled = isDevelopment;
    }

    /**
     * Format message with namespace
     *
     * @param {string} level - Log level
     * @param {string} message - Message to log
     * @return {string} Formatted message
     */
    format(level, message) {
        return `[${this.namespace}:${level}] ${message}`;
    }

    /**
     * Log info message
     *
     * @param {string} message - Message to log
     * @param {...any} args - Additional arguments
     */
    info(message, ...args) {
        if (this.enabled) {
            console.log(this.format('INFO', message), ...args);
        }
    }

    /**
     * Log warning message
     *
     * @param {string} message - Message to log
     * @param {...any} args - Additional arguments
     */
    warn(message, ...args) {
        if (this.enabled) {
            console.warn(this.format('WARN', message), ...args);
        }
    }

    /**
     * Log error message
     *
     * @param {string} message - Message to log
     * @param {...any} args - Additional arguments
     */
    error(message, ...args) {
        // Avoid polluting the browser console in production.
        if (this.enabled) {
            console.error(this.format('ERROR', message), ...args);
        }
    }

    /**
     * Log debug message
     *
     * @param {string} message - Message to log
     * @param {...any} args - Additional arguments
     */
    debug(message, ...args) {
        if (this.enabled) {
            console.debug(this.format('DEBUG', message), ...args);
        }
    }
}

// Export singleton instance
export default new Logger();
